import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Coins, ArrowDownLeft, ArrowUpRight, RotateCcw, CircleAlert, Users, Scale,
  FileText, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { extractError } from '@/lib/errors';
import { formatDate, formatINR } from '@/lib/format';

const ALL = 'all';

// ---------------------------------------------------------------------------
// Gold purity view (999 ↔ 995). Every gold figure is stored in the canonical
// 999 basis. Per approved spec (mirrors the Goldsmith / Loose gold ledgers):
// 999 = 100% pure, 995 = 99.5%, so to hold the SAME pure gold the 995 figure
// is slightly higher:  grams_995 = grams_999 / 0.995
// The per-gram rate moves inversely so value stays invariant on toggle:
//   rate_995 = rate_999 × 0.995
// ---------------------------------------------------------------------------
const PURITY_KEY = 'cgr.purityView';

function readPurity() {
  try { return localStorage.getItem(PURITY_KEY) === '995' ? '995' : '999'; }
  catch { return '999'; }
}

function convG(g999, purity) {
  const n = Number(g999) || 0;
  return purity === '995' ? n / 0.995 : n;
}

function convRate(rate999, purity) {
  const r = Number(rate999) || 0;
  return purity === '995' ? r * 0.995 : r;
}

function g3(n) {
  return Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

// Segmented Gold 999 / Gold 995 toggle (amber-themed to match gold accents).
function GoldPurityToggle({ purity, onChange, testId = 'cgr-purity-toggle' }) {
  const opts = [
    { v: '999', label: 'Gold 999', hint: '100%' },
    { v: '995', label: 'Gold 995', hint: '99.5%' },
  ];
  return (
    <div
      className="inline-flex items-center rounded-md border border-amber-300 overflow-hidden text-[11px] shadow-sm"
      role="group"
      aria-label="Gold purity view"
      data-testid={testId}
    >
      {opts.map((o) => {
        const active = purity === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            aria-pressed={active}
            className={`px-3 py-1.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${
              active ? 'bg-amber-500 text-white' : 'bg-white text-slate-600 hover:bg-amber-50'
            }`}
            data-testid={`${testId}-${o.v}`}
          >
            {o.label} <span className="opacity-70">({o.hint})</span>
          </button>
        );
      })}
    </div>
  );
}

export default function CustomerGoldLedgerReport() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="Customer Gold Ledger Report"
      subtitle="Gold received from and returned to customers — grouped by customer with subtotals."
    >
      <CompanyGate>
        <ReportContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function ReportContent({ companyId }) {
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', customerId: ALL, direction: ALL, advanceOnly: false });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // View controls.
  const [purity, setPurity] = useState(readPurity);
  const [showRate, setShowRate] = useState(true);
  const [showValue, setShowValue] = useState(true);
  // PDF branding — default OFF per product owner.
  const [branding, setBranding] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(PURITY_KEY, purity); } catch { /* noop */ }
  }, [purity]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(false);
    try {
      const params = { company_id: companyId };
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.customerId && filters.customerId !== ALL) params.customer_id = filters.customerId;
      if (filters.direction && filters.direction !== ALL) params.direction = filters.direction;
      if (filters.advanceOnly) params.advance_only = true;
      const { data: res } = await api.get('/customers/gold-ledger-report', { params });
      setData(res);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [companyId, filters.dateFrom, filters.dateTo, filters.customerId, filters.direction, filters.advanceOnly]);

  useEffect(() => { load(); }, [load]);

  const canValues = !!data?.can_view_values;
  const customers = data?.customers || [];
  const groups = data?.groups || [];
  const grand = data?.grand_total || {};
  const showGrand = !!data?.multiple_customers;

  // Effective column visibility (values are gated by permission first).
  const showRateCol = canValues && showRate;
  const showValueCol = canValues && showValue;
  const unit = purity === '995' ? '995' : '999';
  // Advance-only mode reframes the two gold columns as Net Advance / Adjusted
  // Return (the return gold folded against each advance entry).
  const advanceOnly = !!filters.advanceOnly;

  const resetFilters = () => setFilters({ dateFrom: '', dateTo: '', customerId: ALL, direction: ALL, advanceOnly: false });

  const periodLabel = useMemo(() => {
    const from = filters.dateFrom ? formatDate(`${filters.dateFrom}T00:00:00`) : 'Beginning';
    const to = filters.dateTo ? formatDate(`${filters.dateTo}T00:00:00`) : 'Today';
    return `${from} → ${to}`;
  }, [filters.dateFrom, filters.dateTo]);

  // +1 for the always-present Note column (Wave 4.x).
  const colCount = 6 + (showRateCol ? 1 : 0) + (showValueCol ? 1 : 0) + 1;

  const runPdf = useCallback(async (mode) => {
    if (!companyId) return;
    setPdfBusy(true);
    try {
      const params = {
        company_id: companyId,
        purity,
        include_branding: branding,
        show_rate: showRate,
        show_value: showValue,
      };
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.customerId && filters.customerId !== ALL) params.customer_id = filters.customerId;
      if (filters.direction && filters.direction !== ALL) params.direction = filters.direction;
      if (filters.advanceOnly) params.advance_only = true;
      const resp = await api.get('/customers/gold-ledger-report/pdf', { params, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (mode === 'view') {
        const w = window.open(url, '_blank', 'noopener,noreferrer');
        if (!w) toast.error('Popup blocked. Allow popups to view the PDF.');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Customer_Gold_Ledger_Report.pdf';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (e) {
      toast.error(extractError(e, 'Failed to generate PDF'));
    } finally {
      setPdfBusy(false);
    }
  }, [companyId, purity, branding, showRate, showValue, filters]);

  return (
    <div className="space-y-5" data-testid="customer-gold-report">
      {/* Summary chips */}
      {filters.advanceOnly ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-4 border-amber-200 bg-amber-50/70" data-testid="cgr-summary-advance-received">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-100 text-amber-700">
                <Coins size={18} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-amber-800/80 font-medium">Advance Received ({unit})</div>
                <div className="text-xl font-semibold text-amber-800 font-mono tabular-nums">{g3(convG(grand.advance_received_g999, purity))} g</div>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-rose-200 bg-rose-50/60" data-testid="cgr-summary-advance-adjusted">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-rose-100 text-rose-700">
                <ArrowUpRight size={18} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-rose-800/80 font-medium">Adjusted by Returns ({unit})</div>
                <div className="text-xl font-semibold text-rose-800 font-mono tabular-nums">{g3(convG(grand.advance_adjusted_g999, purity))} g</div>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-[var(--divider)] bg-[var(--gold-bg)]" data-testid="cgr-summary-advance-net">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/70 text-[var(--gold-icon)]">
                <Scale size={18} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-[var(--gold-icon)]/80 font-medium">Net Advance Received ({unit})</div>
                <div className="text-xl font-semibold text-[var(--ink-navy)] font-mono tabular-nums">{g3(convG(grand.advance_net_g999, purity))} g</div>
              </div>
            </div>
          </Card>
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4 border-emerald-200 bg-emerald-50/60" data-testid="cgr-summary-received">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-100 text-emerald-700">
              <ArrowDownLeft size={18} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-emerald-800/80 font-medium">Gold Received ({unit})</div>
              <div className="text-xl font-semibold text-emerald-800 font-mono tabular-nums">{g3(convG(grand.received_g999, purity))} g</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-rose-200 bg-rose-50/60" data-testid="cgr-summary-issued">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-rose-100 text-rose-700">
              <ArrowUpRight size={18} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-rose-800/80 font-medium">Gold Returned ({unit})</div>
              <div className="text-xl font-semibold text-rose-800 font-mono tabular-nums">{g3(convG(grand.issued_g999, purity))} g</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-[var(--divider)] bg-[var(--gold-bg)]" data-testid="cgr-summary-net">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/70 text-[var(--gold-icon)]">
              <Scale size={18} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-[var(--gold-icon)]/80 font-medium">Net Held ({unit})</div>
              <div className="text-xl font-semibold text-[var(--ink-navy)] font-mono tabular-nums">{g3(convG(grand.net_g999, purity))} g</div>
            </div>
          </div>
        </Card>
      </div>
      )}

      {/* Filter bar */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[11px] text-slate-600 mb-1 block">From date</Label>
            <DatePicker
              value={filters.dateFrom}
              maxDate={filters.dateTo || undefined}
              onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
              className="h-9 w-44"
              data-testid="cgr-filter-date-from"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-600 mb-1 block">To date</Label>
            <DatePicker
              value={filters.dateTo}
              minDate={filters.dateFrom || undefined}
              onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
              className="h-9 w-44"
              data-testid="cgr-filter-date-to"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-600 mb-1 block">Customer</Label>
            <Select value={filters.customerId} onValueChange={(v) => setFilters((f) => ({ ...f, customerId: v }))}>
              <SelectTrigger className="h-9 w-56" data-testid="cgr-filter-customer">
                <SelectValue placeholder="All customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All customers</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-slate-600 mb-1 block">Type</Label>
            <Select value={filters.direction} onValueChange={(v) => setFilters((f) => ({ ...f, direction: v }))} disabled={filters.advanceOnly}>
              <SelectTrigger className="h-9 w-44" data-testid="cgr-filter-direction">
                <SelectValue placeholder="Received & Returned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Received &amp; Returned</SelectItem>
                <SelectItem value="in">Received only</SelectItem>
                <SelectItem value="out">Returned only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-slate-600 mb-1 block">Advance</Label>
            <label
              className={`flex items-center gap-2 h-9 px-3 rounded-md border cursor-pointer select-none ${filters.advanceOnly ? 'border-amber-400 bg-amber-100 text-amber-800' : 'border-[var(--divider)] bg-white text-slate-600'}`}
              data-testid="cgr-filter-advance-only"
            >
              <Checkbox
                checked={filters.advanceOnly}
                onCheckedChange={(v) => setFilters((f) => ({ ...f, advanceOnly: !!v }))}
                data-testid="cgr-filter-advance-checkbox"
              />
              <span className="text-xs font-medium inline-flex items-center gap-1"><Coins size={13} /> Advance Gold only</span>
            </label>
          </div>
          <Button variant="ghost" className="h-9 text-slate-500" onClick={resetFilters} data-testid="cgr-filter-reset">
            <RotateCcw size={14} className="mr-1.5" /> Reset
          </Button>
          <div className="ml-auto text-xs text-slate-500 self-center" data-testid="cgr-period-label">
            Period: <span className="font-medium text-[var(--ink-navy)]">{periodLabel}</span>
          </div>
        </div>

        {/* View + export controls */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mt-3 pt-3 border-t border-[var(--divider)]" data-testid="cgr-view-controls">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-600 font-medium">Purity view</span>
            <GoldPurityToggle purity={purity} onChange={setPurity} />
          </div>

          {canValues && (
            <div className="flex items-center gap-3" data-testid="cgr-column-toggles">
              <span className="text-[11px] text-slate-600 font-medium">Columns</span>
              <label className="flex items-center gap-1.5 text-[12.5px] text-slate-700 cursor-pointer select-none">
                <Checkbox checked={showRate} onCheckedChange={(v) => setShowRate(!!v)} data-testid="cgr-toggle-rate" />
                Rate
              </label>
              <label className="flex items-center gap-1.5 text-[12.5px] text-slate-700 cursor-pointer select-none">
                <Checkbox checked={showValue} onCheckedChange={(v) => setShowValue(!!v)} data-testid="cgr-toggle-value" />
                Value
              </label>
            </div>
          )}

          <div className="flex items-center gap-2 ml-auto">
            <label className="flex items-center gap-1.5 text-[12.5px] text-slate-700 cursor-pointer select-none" title="Print the company logo & name on the PDF header">
              <Checkbox checked={branding} onCheckedChange={(v) => setBranding(!!v)} data-testid="cgr-toggle-branding" />
              Branding
            </label>
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              disabled={pdfBusy || loading || error || groups.length === 0}
              onClick={() => runPdf('view')}
              data-testid="cgr-view-pdf"
            >
              <FileText size={14} className="mr-1.5" /> View PDF
            </Button>
            <Button
              size="sm"
              className="h-9 luxury-button"
              disabled={pdfBusy || loading || error || groups.length === 0}
              onClick={() => runPdf('download')}
              data-testid="cgr-download-pdf"
            >
              <Download size={14} className="mr-1.5" /> {pdfBusy ? 'Preparing…' : 'Download PDF'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ledger-table w-full text-sm" data-testid="cgr-table">
            <thead>
              <tr className="text-left">
                <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)]">Date</th>
                <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)]">Type</th>
                <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)]">Purity</th>
                <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)] text-right">Weight (g)</th>
                <th className="px-3 py-2.5 font-semibold text-emerald-800 text-right">{advanceOnly ? `Net Advance (${unit})` : `Gold Received (${unit})`}</th>
                <th className="px-3 py-2.5 font-semibold text-rose-800 text-right">{advanceOnly ? `Adjusted Return (${unit})` : `Gold Returned (${unit})`}</th>
                {showRateCol && <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)] text-right">Rate (ref)</th>}
                {showValueCol && <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)] text-right">Value (ref)</th>}
                <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)]">Note</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} data-testid="cgr-loading-row">
                    {Array.from({ length: colCount }).map((__, j) => (
                      <td key={j} className="px-3 py-2.5"><Skeleton className="h-4 w-full" /></td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={colCount} className="px-3 py-16 text-center" data-testid="cgr-error">
                    <CircleAlert className="mx-auto mb-3 text-rose-500" size={28} />
                    <div className="text-sm text-slate-600 mb-3">Couldn’t load the report.</div>
                    <Button variant="outline" size="sm" onClick={load}>Retry</Button>
                  </td>
                </tr>
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="px-3 py-16 text-center" data-testid="cgr-empty">
                    <Users className="mx-auto mb-3 text-slate-300" size={30} />
                    <div className="text-[var(--ink-navy)] font-medium">No customer gold movements</div>
                    <div className="text-sm text-slate-500 mt-1">No gold has been received from or returned to customers for the selected filters.</div>
                  </td>
                </tr>
              ) : (
                groups.map((grp) => (
                  <React.Fragment key={grp.customer_id}>
                    {/* Group header */}
                    <tr className="bg-[var(--paper-warm)]" data-testid={`cgr-group-${grp.customer_id}`}>
                      <td colSpan={colCount} className="px-3 py-2 border-t border-[var(--divider)]">
                        <div className="flex items-center gap-2">
                          <Coins size={14} className="text-[var(--gold-icon)]" />
                          <span className="font-semibold text-[var(--ink-navy)]">{grp.customer_name}</span>
                          <Badge variant="secondary" className="text-[10px]">{grp.subtotal?.entry_count || grp.rows.length} entries</Badge>
                        </div>
                      </td>
                    </tr>
                    {/* Rows */}
                    {grp.rows.map((r) => {
                      const isIn = r.direction === 'in';
                      const purityText = r.purity_name ? `${r.purity_name} (${Number(r.purity_percent).toFixed(2)}%)` : `${Number(r.purity_percent).toFixed(2)}%`;
                      return (
                        <tr key={r.id} data-testid="cgr-row">
                          <td className="px-3 py-2 text-slate-600 font-mono tabular-nums whitespace-nowrap">{formatDate(r.timestamp)}</td>
                          <td className="px-3 py-2">
                            {isIn ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                                <ArrowDownLeft size={13} /> Received
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-medium">
                                <ArrowUpRight size={13} /> Returned
                              </span>
                            )}
                            {isIn && r.advance_gold && (
                              <Badge className="ml-1.5 text-[9px] border border-amber-300 bg-amber-100 text-amber-800" data-testid="cgr-row-advance-badge">
                                <Coins size={9} className="mr-0.5" /> Advance
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-600">{purityText}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-700">{g3(r.weight_grams)}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-700">
                            {advanceOnly ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <span>{g3(convG(r.advance_net_g999, purity))}</span>
                                {Number(r.advance_consumed_g999) > 0 && (
                                  <Badge className="text-[9px] border border-[var(--gold-border)] bg-[var(--gold-bg)] text-[var(--gold-icon)] font-medium" data-testid={`cgr-row-net-badge-${r.id}`}>
                                    Net balance
                                  </Badge>
                                )}
                              </div>
                            ) : (isIn ? g3(convG(r.received_g999, purity)) : '—')}
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-rose-700" data-testid={advanceOnly ? `cgr-row-adjusted-${r.id}` : undefined}>
                            {advanceOnly
                              ? (Number(r.advance_consumed_g999) > 0 ? g3(convG(r.advance_consumed_g999, purity)) : '—')
                              : (!isIn ? g3(convG(r.issued_g999, purity)) : '—')}
                          </td>
                          {showRateCol && <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-600">{r.rate_per_gram_999 != null ? formatINR(convRate(r.rate_per_gram_999, purity)) : '—'}</td>}
                          {showValueCol && <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-600">{r.value != null ? formatINR(r.value) : '—'}</td>}
                          <td className="px-3 py-2 text-slate-600 max-w-[240px] whitespace-pre-wrap break-words" data-testid={`cgr-row-note-${r.id}`}>{r.note ? r.note : <span className="text-slate-300">—</span>}</td>
                        </tr>
                      );
                    })}
                    {/* Subtotal */}
                    <tr className="bg-[color-mix(in_srgb,var(--gold-bg)_50%,#fff)] font-medium" data-testid={`cgr-subtotal-${grp.customer_id}`}>
                      <td className="px-3 py-2 text-[var(--ink-navy)]" colSpan={3}>Subtotal — {grp.customer_name}</td>
                      <td className="px-3 py-2 text-right text-slate-500">&nbsp;</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-800" data-testid={`cgr-subtotal-received-${grp.customer_id}`}>{g3(convG(advanceOnly ? grp.subtotal?.advance_net_g999 : grp.subtotal?.received_g999, purity))}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-rose-800" data-testid={`cgr-subtotal-issued-${grp.customer_id}`}>{g3(convG(advanceOnly ? grp.subtotal?.advance_adjusted_g999 : grp.subtotal?.issued_g999, purity))}</td>
                      {showRateCol && <td className="px-3 py-2 text-right text-slate-500">&nbsp;</td>}
                      {showValueCol && <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-700">{grp.subtotal?.issued_value != null ? formatINR((grp.subtotal?.received_value || 0) - (grp.subtotal?.issued_value || 0)) : '—'}</td>}
                      <td className="px-3 py-2">&nbsp;</td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
            {showGrand && !loading && !error && (
              <tfoot>
                <tr className="bg-[var(--ink-navy)] text-white" data-testid="cgr-grand-total">
                  <td className="px-3 py-3 font-semibold" colSpan={3}>Grand Total ({groups.length} customers)</td>
                  <td className="px-3 py-3 text-right">&nbsp;</td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums font-semibold" data-testid="cgr-grand-received">{g3(convG(advanceOnly ? grand.advance_net_g999 : grand.received_g999, purity))}</td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums font-semibold" data-testid="cgr-grand-issued">{g3(convG(advanceOnly ? grand.advance_adjusted_g999 : grand.issued_g999, purity))}</td>
                  {showRateCol && <td className="px-3 py-3 text-right">&nbsp;</td>}
                  {showValueCol && <td className="px-3 py-3 text-right font-mono tabular-nums font-semibold">{formatINR((grand.received_value || 0) - (grand.issued_value || 0))}</td>}
                  <td className="px-3 py-3">&nbsp;</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      <p className="text-[11px] text-slate-400">
        Rate &amp; value are reference figures (client-agreed) and do not post to any receivables ledger. Gold quantities are shown in grams ({unit} basis).
      </p>
    </div>
  );
}
